import React from 'react';
import ComparisonColumnHeader from './ComparisonColumnHeader';
import SectionHeader from './SectionHeader';
import MetricRow from './MetricRow';
import EditableRow from './EditableRow';
import { Plus } from 'lucide-react';
import { computeWinsByColumn } from './comparisonHelpers';
import './ComparisonTable.css';

const ComparisonTable = ({
    columns,
    propertiesMap,
    perColumnAssumptions,
    results,
    onUpdateColumn,
    onRemoveColumn,
    savedScenarios,
    onLoadScenario,
    maxReached,
    onOpenAddModal,
    defaultPerColumnAssumptions,
}) => {
    const columnIds = columns.map(c => c.columnId);
    const gridCols = `180px repeat(${columns.length}, 1fr)`;
    const winsByColumn = computeWinsByColumn(results, columnIds);
    const topWins = Math.max(0, ...Object.values(winsByColumn));

    return (
        <div className="ct-wrapper">
            <div className="ct-grid" style={{ gridTemplateColumns: gridCols }}>
                {/* Column Headers */}
                <div className="ct-corner">
                    {!maxReached && (
                        <button className="ct-add-col" onClick={onOpenAddModal} aria-label="Add column">
                            <Plus size={18} />
                        </button>
                    )}
                </div>
                {columns.map(col => (
                    <ComparisonColumnHeader
                        key={col.columnId}
                        property={propertiesMap[col.propertyId]}
                        scenarioName={col.scenarioName}
                        onRemove={() => onRemoveColumn(col.columnId)}
                        savedScenarios={savedScenarios[col.propertyId] || []}
                        onLoadScenario={(scenario) => onLoadScenario(col.columnId, scenario)}
                        wins={winsByColumn[col.columnId]}
                        isTopPick={columns.length > 1 && winsByColumn[col.columnId] === topWins && topWins > 0}
                    />
                ))}

                {/* Deal Rating */}
                <SectionHeader label="Deal Rating" />
                <MetricRow label="Cash on Cash ROI" metricKey="cashOnCashROI" suffix="%" decimals={1} results={results} columnIds={columnIds} highlight="max" />
                <MetricRow label="Deal Rating" metricKey="dealRating" type="badge" results={results} columnIds={columnIds} />

                {/* Purchase Details (editable) */}
                <SectionHeader label="Purchase Details" />
                <EditableRow label="Offer Price" field="purchasePrice" prefix="$" perColumnAssumptions={perColumnAssumptions} columnIds={columnIds} onUpdate={onUpdateColumn} defaultPerColumn={defaultPerColumnAssumptions} />
                <EditableRow label="Improvements" field="improvements" prefix="$" perColumnAssumptions={perColumnAssumptions} columnIds={columnIds} onUpdate={onUpdateColumn} defaultPerColumn={defaultPerColumnAssumptions} />
                <EditableRow label="Closing Costs" field="closingCosts" prefix="$" perColumnAssumptions={perColumnAssumptions} columnIds={columnIds} onUpdate={onUpdateColumn} defaultPerColumn={defaultPerColumnAssumptions} />

                {/* Revenue (editable) */}
                <SectionHeader label="Revenue" />
                <EditableRow label="Rent /mo" field="rentPerUnit" prefix="$" perColumnAssumptions={perColumnAssumptions} columnIds={columnIds} onUpdate={onUpdateColumn} defaultPerColumn={defaultPerColumnAssumptions} />
                <EditableRow label="Units" field="numberOfUnits" perColumnAssumptions={perColumnAssumptions} columnIds={columnIds} onUpdate={onUpdateColumn} defaultPerColumn={defaultPerColumnAssumptions} />

                {/* Key Metrics */}
                <SectionHeader label="Key Metrics" />
                <MetricRow label="Monthly Cash Flow" metricKey="monthlyCashFlow" type="currency" results={results} columnIds={columnIds} highlight="max" />
                <MetricRow label="Monthly Mortgage" metricKey="monthlyMortgage" type="currency" results={results} columnIds={columnIds} highlight="min" />
                <MetricRow label="NOI (Annual)" metricKey="netOperatingIncome" type="currency" results={results} columnIds={columnIds} highlight="max" />
                <MetricRow label="Cap Rate" metricKey="capRate" suffix="%" decimals={1} results={results} columnIds={columnIds} highlight="max" />
                <MetricRow label="Total ROI" metricKey="totalROI" suffix="%" decimals={1} results={results} columnIds={columnIds} highlight="max" />
                <MetricRow label="Gross Rental Income" metricKey="grossRentalIncome" type="currency" results={results} columnIds={columnIds} highlight="max" />
                <MetricRow label="Operating Expenses" metricKey="totalOperatingExpenses" type="currency" results={results} columnIds={columnIds} highlight="min" />

                {/* Cash Required */}
                <SectionHeader label="Cash Required" />
                <MetricRow label="Cash to Close" metricKey="cashInvested" type="currency" results={results} columnIds={columnIds} highlight="min" />
                <MetricRow label="Down Payment" metricKey="downPaymentAmount" type="currency" results={results} columnIds={columnIds} />
                <MetricRow label="Loan Amount" metricKey="loanAmount" type="currency" results={results} columnIds={columnIds} />

                {/* 10-Year Projection */}
                <SectionHeader label="10-Year Projection" />
                <MetricRow label="Year 5 Equity" metricKey="equity" source="equityData" yearIndex={4} type="currency" results={results} columnIds={columnIds} highlight="max" />
                <MetricRow label="Year 10 Equity" metricKey="equity" source="equityData" yearIndex={9} type="currency" results={results} columnIds={columnIds} highlight="max" />
                <MetricRow label="Year 5 Cash Flow" metricKey="cashFlow" source="cashFlowProjection" yearIndex={4} type="currency" results={results} columnIds={columnIds} highlight="max" />
                <MetricRow label="Year 10 Cash Flow" metricKey="cashFlow" source="cashFlowProjection" yearIndex={9} type="currency" results={results} columnIds={columnIds} highlight="max" />
            </div>
        </div>
    );
};

export default ComparisonTable;
